import React, { FC, ReactNode, useEffect, useMemo, useState } from 'react';
import { Col, Row } from 'react-flexbox-grid';
import { Alert } from '@nbc-design/alert';
import { AttentionFilledColor } from '@nbc-design/icons/lib/web/AttentionFilledColor';
import { SecureFill } from '@nbc-design/icons/lib/web/SecureFill';
import get from 'lodash/get';
import { getTextFactory } from 'utils/TextUtils';
import { createErrorFormatter, isJointMenu } from 'utils/productFormUtils';
import { sendTechnicalErrorAnalyticsEvent } from 'services/analytics';
import { GenericProps, Properties } from 'types/interfaces';
import Title from 'components/Title';
import IdentityWrapper from './components/IdentityWrapper';
import SafeIdConsent from './components/SafeIdConsent';
import ValidAlternate from './components/ValidAlternate';
import DocuSignBox from './components/DocuSignBox';
import { ProductFormCustomContentRadioSelect } from 'components/productFormFields';
import Captcha from 'components/Captcha';
import { saveCaptchaResponse } from 'utils';
import { IdentityVerificationMethod } from 'containers/ESign/constants';
import { DECISION_SUCCESS, DECISSION_FAILURE } from './constants';
import { SiteKeyType } from 'components/Captcha/Captcha';

import './styles.scss';

type IdentityValidationProps = GenericProps & {
  handleSubmit: Function;
  indvIdentPrefMethod: string;
  canBypassSafeID: boolean;
  nextStepCode: string;
  submit: Function;
  goToStep: (step: string) => void;
};

type CustomContentDictionary = {
  [key: string]: {
    description: ReactNode[];
    customContent: ReactNode;
  };
};

const resolveAnalyticsMessage = (
  decision: string | undefined,
  indvIdentPrefMethod: string,
  technicalErrorSent: boolean,
): string => {
  if (technicalErrorSent) return '';
  if (decision === DECISSION_FAILURE) return 'safeIdFailure';
  if (decision === undefined && indvIdentPrefMethod === IdentityVerificationMethod.ALTERNATE) return 'safeIdDown';
  return '';
};

const IdentityValidation: FC<IdentityValidationProps> = (props: IdentityValidationProps) => {
  const {
    intl,
    productForm,
    change,
    submit,
    handleSubmit,
    indvIdentPrefMethod,
    canBypassSafeID,
    nextStepCode,
    meta,
    goToStep,
  } = props;

  const getText = getTextFactory(intl, 'identityValidation');
  const getAccessibilityText = getTextFactory(intl, 'accessibility.icon');
  const buttonText = getTextFactory(intl, 'global.button');
  const formatMessageError = () => createErrorFormatter(intl);

  const bankingAccountSchema: { properties: Properties } = get(productForm, 'schema.definitions.BankingAccountUI');
  const indvIdentPrefMethodRefData = get(productForm, 'schema.properties.indvIdentPrefMethod.refData', []);
  const isSafeIdAvailable = indvIdentPrefMethodRefData.some((method) => method.valueDomainCode === 'ONLINE');

  const safeIdUrl: string = get(productForm, 'values.safeIdUrl');
  const decision: string | undefined = get(productForm, 'values.decision');

  const isSafeIdFailed = decision === DECISSION_FAILURE;

  const [currentMethod, setCurrentMethod] = useState(indvIdentPrefMethod);
  const [technicalErrorSent, setTechnicalErrorSent] = useState(false);

  useEffect(() => {
    if (safeIdUrl) {
      window.location.assign(safeIdUrl);
    }
  }, [safeIdUrl]);

  useEffect(() => {
    if (canBypassSafeID && nextStepCode) {
      goToStep(nextStepCode);
    }
  }, [canBypassSafeID, nextStepCode, goToStep]);

  useEffect(() => {
    if (decision === DECISION_SUCCESS) {
      submit();
    }
  }, [decision, submit]);

  useEffect(() => {
    const msg = resolveAnalyticsMessage(decision, indvIdentPrefMethod, technicalErrorSent);
    if (msg !== '') {
      sendTechnicalErrorAnalyticsEvent(msg);
      setTechnicalErrorSent(true);
    }
  }, [decision, technicalErrorSent, indvIdentPrefMethod]);

  const handleCaptchaSuccess = (token: string) => {
    saveCaptchaResponse(token);
    handleSubmit();
  };

  const continueAction = () => {
    if (currentMethod === IdentityVerificationMethod.ONLINE) {
      (window as any).grecaptcha.reset();
      (window as any).grecaptcha.execute();
    } else {
      handleSubmit();
    }
  };

  const handlePrefMethodChange = () => {
    setCurrentMethod((prev) =>
      prev === IdentityVerificationMethod.ONLINE
        ? IdentityVerificationMethod.ALTERNATE
        : IdentityVerificationMethod.ONLINE,
    );
  };

  const customContentDictionary: CustomContentDictionary = useMemo(
    () => ({
      ONLINE: {
        description: [
          <div key="description-online" className="long-description first">
            {getText('onlineDescription')}
          </div>,
        ],
        customContent: <SafeIdConsent intl={intl} change={change} key="safeIdConsent" />,
      },
      ALTERNATE: {
        description: [
          <div key="description-alternate" className="long-description first">
            {getText('otherDescription')}
          </div>,
        ],
        customContent: (
          <ValidAlternate
            key="validAlternate"
            intl={intl}
            change={change}
            bankingAccountSchema={bankingAccountSchema}
            productForm={productForm}
          />
        ),
      },
    }),
    [intl, change, getText, bankingAccountSchema, productForm],
  );

  const isMultiHolderMode = isJointMenu(meta);
  const showSafeIdWarning = !isSafeIdAvailable && !isMultiHolderMode;
  const warningTitleKey = isSafeIdFailed ? 'safeIdFailure' : 'safeIdDown';
  const warningDescriptionKey = isSafeIdFailed ? 'safeIdFailureDescription' : 'safeIdUnavailableDescription';

  return (
    <div className="identity-validation-form">
      <Title label={getText('pageTitle')} subTitle={getText('subTitle')} tabTitle={getText('tabTitle')} />
      <Row className="security-explanation-row">
        <Col xs={12} md={12} lg={12} className="security-explanation-col" data-test="security-explanation-text">
          <Alert
            title={getText('securityInformation')}
            size="small"
            icon={<SecureFill size="small" title={getText('securityInformation')} />}
          />
        </Col>
      </Row>
      <Row>
        <Col xs={12} md={12} lg={12}>
          <IdentityWrapper
            title={getText('validateTitle')}
            subTitle={getText('validateDescription')}
            continueText={buttonText('continue')}
            iconName="selfie"
            hasButton
            buttonType="button"
            buttonAction={continueAction}
          >
            {showSafeIdWarning && (
              <Alert
                key="safeIdConsent-readOnly"
                icon={<AttentionFilledColor size="small" title={getAccessibilityText('alert')} />}
                title={getText(warningTitleKey)}
                description={getText(warningDescriptionKey)}
                appearance="warning"
                className="alert-fail-warning"
              />
            )}
            <ProductFormCustomContentRadioSelect
              label=""
              name="indvIdentPrefMethod"
              customContentDictionary={customContentDictionary}
              isContentInside
              formatErrorMessage={formatMessageError()}
              onChange={handlePrefMethodChange}
            />
          </IdentityWrapper>
          <DocuSignBox title={getText('signer')} />
          <Captcha
            siteKeyType={SiteKeyType.safeid}
            success={handleCaptchaSuccess}
            timeout={() => {}}
            failure={() => {}}
            invisible
          />
        </Col>
      </Row>
    </div>
  );
};

export default IdentityValidation;
